import React, { useCallback, useState } from 'react';
import { View, Pressable, useWindowDimensions, type LayoutChangeEvent, type ViewStyle } from 'react-native';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { AntDesign, Entypo, FontAwesome } from '@expo/vector-icons';
import { ThemeProvider, createTheme, type Theme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { MultiSectionDigitalClock } from '@mui/x-date-pickers/MultiSectionDigitalClock';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { Modal, Text } from '@constants/Themed';
import { getPillButtonStyle } from '@stylesheet';
import { getSelectPalette, SELECT_RADIUS, SELECT_TRIGGER_HEIGHT } from '@components/Global/select';
import { responsiveFontSize } from '@utils/responsiveFontSize';

/** Below this trigger width the date and time stack on two lines. */
const COMPACT_TRIGGER_WIDTH = 300;
/** Modal content width at which calendar and clock sit side by side. */
const SIDE_BY_SIDE_MIN_WIDTH = 520;
const MODAL_MAX_WIDTH = 640;
const CALENDAR_MAX_WIDTH = 340;

const pickerThemeCache = new Map<boolean, Theme>();

/** MUI theme for date/time pickers that follows the app light/dark palette. */
export function getPickerMuiTheme(theme: boolean): Theme {
  const cached = pickerThemeCache.get(theme);
  if (cached) return cached;
  const palette = getSelectPalette(theme);
  const muiTheme = createTheme({
    palette: {
      mode: theme ? 'light' : 'dark',
      primary: { main: getPillButtonStyle(theme, 'primary').backgroundColor, contrastText: '#ffffff' },
      background: { paper: palette.menuBg, default: palette.menuBg },
      text: { primary: palette.textPrimary, secondary: palette.textSecondary },
      divider: palette.inputBorder,
    },
    shape: { borderRadius: SELECT_RADIUS },
    typography: { fontFamily: 'inherit' },
  });
  pickerThemeCache.set(theme, muiTheme);
  return muiTheme;
}

export function getPickerTriggerStyle(theme: boolean, hasError?: boolean): ViewStyle {
  const palette = getSelectPalette(theme);
  return {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    width: '100%',
    minHeight: SELECT_TRIGGER_HEIGHT + 4,
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderWidth: 1,
    borderRadius: SELECT_RADIUS,
    borderColor: hasError ? palette.errorBorder : palette.inputBorder,
    backgroundColor: palette.inputBg,
    position: 'relative',
  };
}

/** Modal + calendar sizing derived from the viewport so the picker never overflows. */
export function getPickerModalLayout(windowWidth: number) {
  const modalWidth = Math.max(260, Math.min(windowWidth - 24, MODAL_MAX_WIDTH));
  const padding = windowWidth < 420 ? 12 : 20;
  const contentWidth = modalWidth - padding * 2;
  const sideBySide = contentWidth >= SIDE_BY_SIDE_MIN_WIDTH;
  const calendarWidth = Math.min(contentWidth, CALENDAR_MAX_WIDTH);
  const daySize = Math.max(28, Math.min(36, Math.floor((calendarWidth - 8) / 7) - 4));
  return { modalWidth, padding, contentWidth, sideBySide, calendarWidth, daySize };
}

export function getCalendarSx(calendarWidth: number, daySize: number) {
  return {
    width: calendarWidth,
    maxWidth: '100%',
    height: 'auto',
    margin: 0,
    '& .MuiPickersCalendarHeader-root': { paddingLeft: 1, paddingRight: 0.5 },
    '& .MuiPickersCalendarHeader-label': { fontWeight: 600 },
    '& .MuiDayCalendar-weekDayLabel': { width: daySize, height: daySize },
    '& .MuiPickersDay-root': { width: daySize, height: daySize, fontSize: responsiveFontSize(13) },
    '& .MuiPickersDay-root.Mui-selected': { fontWeight: 600 },
    '& .MuiDayCalendar-slideTransition': { minHeight: (daySize + 4) * 6 },
    '& .MuiYearCalendar-root, & .MuiMonthCalendar-root': { width: '100%' },
  };
}

type DateTimePickerFieldProps = {
  value: moment.Moment | null;
  onChange: (value: moment.Moment | null) => void;
  theme: boolean;
  hasError?: boolean;
  placeholder: string;
  dateFormat?: string;
  /** Hide the time column (e.g. all-day events). */
  dateOnly?: boolean;
  minuteStep?: number;
};

const DateTimePickerField = ({
  value,
  onChange,
  theme,
  hasError,
  placeholder,
  dateFormat = 'DD/MM/YYYY',
  dateOnly = false,
  minuteStep = 5,
}: DateTimePickerFieldProps) => {
  const { t } = useTranslation();
  const { width: windowWidth } = useWindowDimensions();
  const [fieldWidth, setFieldWidth] = useState(0);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<moment.Moment>(() => moment());

  const palette = getSelectPalette(theme);
  const accent = getPillButtonStyle(theme, 'primary').backgroundColor;
  const compact = fieldWidth > 0 && fieldWidth < COMPACT_TRIGGER_WIDTH;
  const hasValue = !!value && value.isValid();
  const layout = getPickerModalLayout(windowWidth);
  const clockHeight = layout.sideBySide ? (layout.daySize + 4) * 6 + 64 : 176;

  const onTriggerLayout = useCallback((e: LayoutChangeEvent) => {
    setFieldWidth(Math.round(e.nativeEvent.layout.width));
  }, []);

  const openPicker = () => {
    const initial = hasValue
      ? value!.clone()
      : moment().add(1, 'hour').startOf('hour');
    setDraft(initial);
    setOpen(true);
  };

  const confirm = () => {
    const next = dateOnly ? draft.clone().startOf('day') : draft.clone().seconds(0).milliseconds(0);
    onChange(next);
    setOpen(false);
  };

  const setNow = () => {
    const now = moment();
    const roundedMinutes = Math.round(now.minutes() / minuteStep) * minuteStep;
    setDraft(now.clone().minutes(roundedMinutes).seconds(0).milliseconds(0));
  };

  const valueTextStyle = {
    color: palette.textPrimary,
    fontSize: responsiveFontSize(15),
    fontWeight: '600' as const,
  };

  return (
    <>
      <View onLayout={onTriggerLayout} style={getPickerTriggerStyle(theme, hasError)}>
        <Pressable
          onPress={openPicker}
          accessibilityRole="button"
          style={({ pressed }) => ({
            flex: 1,
            minWidth: 0,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 10,
            opacity: pressed ? 0.85 : 1,
          })}
        >
          <Entypo name="calendar" size={18} color={hasValue ? palette.textPrimary : palette.placeholder} />
          {hasValue ? (
            <View
              style={{
                flex: 1,
                minWidth: 0,
                flexDirection: compact ? 'column' : 'row',
                alignItems: compact ? 'flex-start' : 'center',
                gap: compact ? 2 : 10,
              }}
            >
              <Text numberOfLines={1} style={valueTextStyle}>
                {value!.format(dateFormat)}
              </Text>
              {!dateOnly && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <AntDesign name="clock-circle" size={13} color={palette.textSecondary} />
                  <Text numberOfLines={1} style={[valueTextStyle, { color: palette.textSecondary, fontWeight: '500' }]}>
                    {value!.format('LT')}
                  </Text>
                </View>
              )}
            </View>
          ) : (
            <Text numberOfLines={1} style={{ flex: 1, color: palette.placeholder, fontSize: responsiveFontSize(15) }}>
              {placeholder}
            </Text>
          )}
        </Pressable>
        {hasValue ? (
          <Pressable
            onPress={() => onChange(null)}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel={t('Clear')}
            style={({ pressed }) => ({ padding: 4, opacity: pressed ? 0.6 : 1 })}
          >
            <Entypo name="cross" size={18} color={palette.placeholder} />
          </Pressable>
        ) : (
          <FontAwesome name="chevron-down" size={12} color={palette.placeholder} style={{ opacity: 0.8 }} />
        )}
      </View>

      {open && (
        <Modal
          isVisible={open}
          setVisible={setOpen}
          onBackdropPress={() => setOpen(false)}
          backgroundColor="transparent"
          hideCloseButton
          style={{ margin: 0, justifyContent: 'center', alignItems: 'center' }}
          backdropOpacity={0.4}
        >
          <View
            style={{
              width: layout.modalWidth,
              maxHeight: '92%',
              backgroundColor: palette.menuBg,
              borderRadius: 16,
              borderWidth: 1,
              borderColor: palette.inputBorder,
              padding: layout.padding,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 8 },
              shadowOpacity: theme ? 0.15 : 0.4,
              shadowRadius: 24,
              elevation: 8,
            }}
          >
            <View style={{ marginBottom: 8 }}>
              <Text style={{ color: palette.textSecondary, fontSize: responsiveFontSize(12), fontWeight: '600' }}>
                {dateOnly ? t('Date') : t('selectDateAndTime')}
              </Text>
              <Text style={{ color: palette.textPrimary, fontSize: responsiveFontSize(18), fontWeight: '700', marginTop: 2 }}>
                {dateOnly ? draft.format(dateFormat) : `${draft.format(dateFormat)} · ${draft.format('LT')}`}
              </Text>
            </View>

            <ThemeProvider theme={getPickerMuiTheme(theme)}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <View
                  style={{
                    flexDirection: layout.sideBySide ? 'row' : 'column',
                    alignItems: layout.sideBySide ? 'flex-start' : 'center',
                    gap: layout.sideBySide ? 16 : 8,
                  }}
                >
                  <DateCalendar
                    value={draft}
                    onChange={(next: moment.Moment | null) => {
                      if (!next || !next.isValid()) return;
                      setDraft((prev) => next.clone().hours(prev.hours()).minutes(prev.minutes()).seconds(0));
                    }}
                    views={['year', 'month', 'day']}
                    openTo="day"
                    sx={getCalendarSx(layout.calendarWidth, layout.daySize)}
                  />
                  {!dateOnly && (
                    <View
                      style={{
                        flex: layout.sideBySide ? 1 : undefined,
                        width: layout.sideBySide ? undefined : '100%',
                        minWidth: 0,
                        borderWidth: 1,
                        borderColor: palette.inputBorder,
                        borderRadius: SELECT_RADIUS,
                        overflow: 'hidden',
                      }}
                    >
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 6,
                          paddingHorizontal: 12,
                          paddingVertical: 8,
                          borderBottomWidth: 1,
                          borderBottomColor: palette.inputBorder,
                        }}
                      >
                        <AntDesign name="clock-circle" size={13} color={palette.textSecondary} />
                        <Text style={{ color: palette.textSecondary, fontSize: responsiveFontSize(12), fontWeight: '600' }}>
                          {t('Time')}
                        </Text>
                      </View>
                      <MultiSectionDigitalClock
                        value={draft}
                        onChange={(next: moment.Moment | null) => {
                          if (!next || !next.isValid()) return;
                          setDraft((prev) => prev.clone().hours(next.hours()).minutes(next.minutes()).seconds(0));
                        }}
                        timeSteps={{ minutes: minuteStep }}
                        sx={{
                          width: '100%',
                          borderBottom: 'none',
                          '& .MuiMultiSectionDigitalClockSection-root': {
                            flex: 1,
                            width: 'auto',
                            maxHeight: clockHeight,
                          },
                          '& .MuiMultiSectionDigitalClockSection-item': {
                            width: 'auto',
                            borderRadius: '9999px',
                            fontSize: responsiveFontSize(13),
                          },
                        }}
                      />
                    </View>
                  )}
                </View>
              </LocalizationProvider>
            </ThemeProvider>

            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 8,
                marginTop: 16,
              }}
            >
              <Pressable onPress={dateOnly ? () => setDraft(moment().startOf('day')) : setNow} style={{ marginRight: 'auto', paddingVertical: 8 }}>
                <Text style={{ color: accent, fontSize: responsiveFontSize(14), fontWeight: '600' }}>
                  {dateOnly ? t('Today') : t('Now')}
                </Text>
              </Pressable>
              <Pressable onPress={() => setOpen(false)} style={[getPillButtonStyle(theme, 'secondary'), { paddingHorizontal: 16 }]}>
                <Text style={{ color: palette.textPrimary, fontSize: responsiveFontSize(14), fontWeight: '600' }}>
                  {t('Cancel')}
                </Text>
              </Pressable>
              <Pressable onPress={confirm} style={[getPillButtonStyle(theme, 'primary'), { paddingHorizontal: 16 }]}>
                <Text style={{ color: '#ffffff', fontSize: responsiveFontSize(14), fontWeight: '600' }}>
                  {t('Done')}
                </Text>
              </Pressable>
            </View>
          </View>
        </Modal>
      )}
    </>
  );
};

export default DateTimePickerField;
